import styled from 'styled-components';
import {
  isPaymentCurrency,
  isValidPaymentAmount,
  type ProductData
} from '@elfsight/lib-stripe-integration';
import { CheckoutComponent } from '@elfsight/widget-checkout';
import { Price } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { meta } from '../../constants';
import { doesPriceSupportPayment } from '../../utils';
import { useFormPopup } from './form-popup-context';
import {
  getFormFunctionalitySettings,
  getProductPriceData,
  useCheckoutDictionary,
  useSummaryTables
} from './utils';

const { alias } = meta;

export function Checkout() {
  const { t } = useLingui();
  const checkoutDictionary = useCheckoutDictionary();

  const settings = useSettings();
  const {
    formHeading: headingText,
    formCaption: captionText,
    checkoutSuccessTitle,
    checkoutSuccessText,
    integrationStripe,
    fields,
    widgetId = '1'
  } = settings;
  const { cancellationPolicyText: footerText } = integrationStripe ?? {};

  const { isPopupOpen, closePopup, currentColumn } = useFormPopup();
  const summaryTables = useSummaryTables();

  if (!currentColumn) {
    return;
  }

  const {
    title: label,
    titleCaption: description,
    price,
    picture
  } = currentColumn;
  const { price: amount, currency } = price;
  const { url: imageSrc } = picture ?? {};

  const isValidPrice =
    doesPriceSupportPayment(price) &&
    isPaymentCurrency(currency) &&
    isValidPaymentAmount({ amount, currency })[0];
  const productPriceData = getProductPriceData(price);
  const productData: ProductData = {
    name: label,
    ...productPriceData
  };
  const paymentMetadata = {
    widgetId,
    paymentContext: alias
  };

  const { id: emailFieldId } =
    fields.find(({ type }) => type === 'email') ?? {};
  const paymentPageSettings = {
    footerText,
    emailFieldId,
    productData,
    paymentMetadata
  };
  const successMessageSettings = {
    heading: checkoutSuccessTitle || t`Payment Successful`,
    description:
      checkoutSuccessText ||
      t`Your payment has been processed successfully. Thank you!`,
    buttonText: t`Ok`
  };

  const derivedPrice = isValidPrice ? price : undefined;
  const checkoutItem = {
    label,
    description,
    imageSrc,
    price: derivedPrice
  };

  const summarySettings = {
    withFirstDivider: false,
    totalPrice: derivedPrice,
    productCard: checkoutItem,
    tables: summaryTables,
    totalLabel: t`Total`
  };

  const commonProps = {
    checkoutItem,
    paymentPageSettings,
    successMessageSettings,
    summarySettings,
    dictionary: checkoutDictionary,
    isOpen: isPopupOpen,
    handleCloseRequest: closePopup,
    PriceComponent: Price
  };

  const formSettings = getFormFunctionalitySettings(settings);
  const formPageSettings = {
    formSettings,
    formLayoutProps: {
      headingText,
      captionText,
      footerText
    }
  };

  return (
    <StyledCheckoutComponent
      {...commonProps}
      formPageSettings={formPageSettings}
      withInternalForm
    />
  );
}

const StyledCheckoutComponent = styled(CheckoutComponent).attrs(
  ({
    theme: {
      form: { successColor, errorColor, fieldColor, fieldActiveColor },
      popup: { background }
    }
  }) => ({
    errorColor,
    successColor,
    textColor: fieldColor,
    accentColor: fieldActiveColor,
    backgroundColor: background?.color
  })
)``;
